import React, { useState, useEffect, useCallback } from 'react';
import ScGrid from '../ScGrid';
import ScModal from '../ScModal';
import ScSpinner from '../ScSpinner';
import { fetchIssueCategories, createIssueCategory, updateIssueCategory } from '../../studyConfigurationApi';
import { DEFAULT_ISSUE_CATEGORIES } from '../../studyConfigurationDefaults';

const emptyForm = () => ({ issueCategoryDescription: '', sortOrder: 10, isActive: true });

const descKey = (description) => (description ?? '').trim();

const IssueCategoriesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchIssueCategories();
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load issue categories.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm(emptyForm()); setModalOpen(true); };
  const openEdit = (row) => {
    setEditId(row.issueCategoryMstNo);
    setForm({
      issueCategoryDescription: row.issueCategoryDescription,
      sortOrder: row.sortOrder,
      isActive: row.isActive,
    });
    setModalOpen(true);
  };

  const save = async () => {
    try {
      if (editId) await updateIssueCategory(editId, { ...form, issueCategoryMstNo: editId });
      else await createIssueCategory(form);
      setModalOpen(false);
      setSuccess(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    }
  };

  const seedDefaultCategories = async () => {
    setError(null);
    setSuccess(null);
    const existing = new Set(rows.map((r) => descKey(r.issueCategoryDescription)));
    const missing = DEFAULT_ISSUE_CATEGORIES.filter(
      (c) => !existing.has(descKey(c.issueCategoryDescription))
    );
    const alreadyCount = DEFAULT_ISSUE_CATEGORIES.length - missing.length;

    if (missing.length === 0) {
      setSuccess('All default categories already exist.');
      return;
    }

    setSeeding(true);
    let inserted = 0;
    let failed = 0;
    let lastError = null;

    for (const category of missing) {
      try {
        await createIssueCategory({
          issueCategoryDescription: category.issueCategoryDescription,
          sortOrder: category.sortOrder,
          isActive: true,
        });
        inserted += 1;
      } catch (e) {
        failed += 1;
        lastError = e.response?.data?.error || 'Insert failed.';
      }
    }

    setSeeding(false);
    await load();

    if (inserted > 0) {
      const parts = [`Inserted ${inserted} categor${inserted === 1 ? 'y' : 'ies'}.`];
      if (alreadyCount > 0) parts.push(`${alreadyCount} already existed.`);
      if (failed > 0) parts.push(`${failed} failed.`);
      setSuccess(parts.join(' '));
      if (failed > 0 && lastError) setError(lastError);
    } else if (lastError) {
      setError(lastError);
    }
  };

  if (loading) return <ScSpinner />;
  return (
    <div>
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <div className="sc-toolbar">
        <button type="button" className="sc-btn sc-btn-primary" onClick={openAdd} disabled={seeding}>
          Add Category
        </button>
        <button
          type="button"
          className="sc-btn sc-btn-secondary"
          onClick={seedDefaultCategories}
          disabled={seeding}
        >
          {seeding ? 'Inserting…' : 'Insert Default Categories'}
        </button>
      </div>
      <ScGrid
        columns={[
          { key: 'issueCategoryDescription', label: 'Description' },
          { key: 'sortOrder', label: 'Sort Order' },
          { key: 'isActive', label: 'Active', render: (r) => (r.isActive ? 'Yes' : 'No') },
        ]}
        rows={rows}
        onEdit={openEdit}
      />
      <ScModal
        open={modalOpen}
        title={editId ? 'Edit Issue Category' : 'Add Issue Category'}
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button type="button" className="sc-btn sc-btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="button" className="sc-btn sc-btn-primary" onClick={save}>Save</button>
          </>
        )}
      >
        <div className="sc-form-row">
          <label>Description</label>
          <input value={form.issueCategoryDescription} onChange={(e) => setForm({ ...form, issueCategoryDescription: e.target.value })} />
        </div>
        <div className="sc-form-row">
          <label>Sort Order</label>
          <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
        </div>
        <div className="sc-form-row sc-check-item">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} id="ic-active" />
          <label htmlFor="ic-active">Active</label>
        </div>
      </ScModal>
    </div>
  );
};

export default IssueCategoriesTab;
